import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Scissors, Search } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { hasPermission } from "../../lib/auth";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import {
  SemaforoDeuda,
  diasDesdeVencimiento,
  mesesDeAtraso,
} from "../../components/SemaforoDeuda";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import { useAuth } from "../auth/useAuth";
import type { OrigenNavegacion as Desde } from "../../components/ui";
import { ETIQUETA_ESTADO_CORTE } from "../cortes/estado";
import type { EstadoOrdenCorte } from "../cortes/api";
import { abrirCortesLote, listarDeudores, type TopDeudor } from "./api";

/** Mismo nombre de estado que en Cortes ("Orden abierta" para Pendiente). */
function etiquetaCorte(estado: string): string {
  return ETIQUETA_ESTADO_CORTE[estado as EstadoOrdenCorte] ?? estado;
}

/** Días de atraso desde la boleta impaga más antigua: el mismo cálculo que
 *  la cuenta corriente. Null si el backend no trae vencimiento. */
function diasAtraso(d: TopDeudor): number | null {
  return diasDesdeVencimiento([d.vencimiento_mas_antiguo]);
}

/** Meses de atraso con el mismo criterio que muestra el semáforo. */
function mesesAtraso(d: TopDeudor): number {
  const dias = diasAtraso(d);
  return dias != null ? mesesDeAtraso(dias) : (d.antiguedad_meses ?? 0);
}

/** Enlace al socio: se ve como enlace (brand-700, subrayado al pasar). */
const LINK_SOCIO =
  "font-medium text-brand-700 hover:text-brand-800 hover:underline focus-visible:underline";

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Un deudor se puede seleccionar para abrir corte en lote si tiene un único
 *  contrato moroso y todavía no tiene una orden de corte activa. */
function elegible(d: TopDeudor): boolean {
  return d.contrato_id != null && d.estado_corte == null;
}

/** Varias casas con deuda: el corte es por arranque y se elige en la ficha. */
function variosArranques(d: TopDeudor): boolean {
  return d.contrato_id == null && d.estado_corte == null;
}

export function MorosidadPage(): JSX.Element {
  const { state } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const puedeGestionar = hasPermission(state, "cortes.gestionar");

  // Los filtros viven en la URL: al volver desde la ficha de un socio
  // (convención `desde`, auditoría NAV-07) la lista queda como estaba.
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const q = params.get("q") ?? "";
  const minMeses = params.get("meses") ?? "";
  const minMonto = params.get("monto") ?? "";
  const soloSinCorte = params.get("sincorte") === "1";
  function setFiltro(clave: string, valor: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (valor) next.set(clave, valor);
        else next.delete(clave);
        return next;
      },
      { replace: true },
    );
  }
  const desde: Desde = {
    etiqueta: "Morosidad",
    ruta: `${location.pathname}${location.search}`,
  };

  const [sel, setSel] = useState<Set<number>>(new Set());
  const [confirmar, setConfirmar] = useState(false);

  const deudoresQ = useQuery({ queryKey: ["morosidad", "deudores"], queryFn: listarDeudores });

  const total = deudoresQ.data?.length ?? 0;
  const filtrados = useMemo(() => {
    const data = deudoresQ.data ?? [];
    const meses = Number(minMeses) || 0;
    const monto = Number(minMonto) || 0;
    return data.filter((d) => {
      if (soloSinCorte && d.estado_corte != null) return false;
      if (mesesAtraso(d) < meses) return false;
      if (d.monto_deuda < monto) return false;
      const t = norm(q.trim());
      if (!t) return true;
      return norm(
        [d.socio_nombre, d.socio_numero, d.sector_nombre].filter(Boolean).join(" "),
      ).includes(t);
    });
  }, [deudoresQ.data, q, minMeses, minMonto, soloSinCorte]);

  // Mapa contrato_id → deudor, para resolver el monto al abrir el lote.
  const porContrato = useMemo(() => {
    const m = new Map<number, TopDeudor>();
    for (const d of deudoresQ.data ?? []) {
      if (d.contrato_id != null) m.set(d.contrato_id, d);
    }
    return m;
  }, [deudoresQ.data]);

  const elegiblesFiltrados = filtrados.filter(elegible);
  const todosSeleccionados =
    elegiblesFiltrados.length > 0 &&
    elegiblesFiltrados.every((d) => sel.has(d.contrato_id as number));

  function toggle(contratoId: number): void {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  }

  function toggleTodos(): void {
    setSel((prev) => {
      const next = new Set(prev);
      if (todosSeleccionados) {
        for (const d of elegiblesFiltrados) next.delete(d.contrato_id as number);
      } else {
        for (const d of elegiblesFiltrados) next.add(d.contrato_id as number);
      }
      return next;
    });
  }

  const abrir = useMutation({
    mutationFn: () => {
      const items = [...sel]
        .map((cid) => {
          const d = porContrato.get(cid);
          return d ? { contrato_id: cid, monto_deuda_al_corte: d.monto_deuda } : null;
        })
        .filter((x): x is { contrato_id: number; monto_deuda_al_corte: number } => x != null);
      return abrirCortesLote(items);
    },
    onSuccess: (r) => {
      toast.success(
        `${r.abiertas} orden(es) de corte abierta(s)` +
          (r.omitidas > 0 ? ` · ${r.omitidas} omitida(s) (ya tenían una orden)` : ""),
      );
      setSel(new Set());
      setConfirmar(false);
      void qc.invalidateQueries({ queryKey: ["morosidad", "deudores"] });
      void qc.invalidateQueries({ queryKey: ["dashboard", "operativo"] });
    },
    onError: (err) => {
      setConfirmar(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudieron abrir los cortes");
    },
  });

  const montoSeleccionado = [...sel].reduce(
    (acc, cid) => acc + (porContrato.get(cid)?.monto_deuda ?? 0),
    0,
  );

  function tituloCheckbox(d: TopDeudor): string {
    if (elegible(d)) return `Seleccionar a ${d.socio_nombre} para corte`;
    if (d.estado_corte != null) return "Ya tiene una orden de corte";
    return "Tiene varias casas con deuda: el corte se abre desde su ficha";
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Morosidad"
        icon={AlertTriangle}
        subtitle={
          deudoresQ.data
            ? `${filtrados.length}${filtrados.length !== total ? ` de ${total}` : ""} deudores`
            : undefined
        }
        actions={
          puedeGestionar && (
            <Button
              icon={Scissors}
              variant="destructive"
              disabled={sel.size === 0}
              onClick={() => setConfirmar(true)}
            >
              Abrir cortes seleccionados ({sel.size})
            </Button>
          )
        }
      />

      <p className="text-sm text-neutral-600">
        Todos los socios con boletas impagas. Filtra por atraso o monto,
        selecciona los que quieres cortar y abre las órdenes de una vez. Los que
        ya tienen una orden en{" "}
        <Link to="/cortes" className="text-brand-700 hover:text-brand-800">
          Cortes
        </Link>{" "}
        aparecen marcados. Si un socio tiene varias casas con deuda, el corte se
        abre desde su ficha, casa por casa.
      </p>

      {total > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-auto">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setFiltro("q", e.target.value)}
              placeholder="Buscar socio, N° o sector…"
              aria-label="Buscar deudor"
              className="w-full sm:w-64 rounded-md border border-neutral-450 py-1.5 pl-8 pr-3 text-sm shadow-sm"
            />
          </div>
          <input
            type="number"
            min={0}
            value={minMeses}
            onChange={(e) => setFiltro("meses", e.target.value)}
            placeholder="≥ meses"
            aria-label="Meses de atraso mínimo"
            className="w-28 rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            title="Meses de atraso mínimo"
          />
          <input
            type="number"
            min={0}
            value={minMonto}
            onChange={(e) => setFiltro("monto", e.target.value)}
            placeholder="≥ deuda $"
            aria-label="Deuda mínima en pesos"
            className="w-32 rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            title="Deuda mínima"
          />
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-neutral-450"
              checked={soloSinCorte}
              onChange={(e) => setFiltro("sincorte", e.target.checked ? "1" : "")}
            />
            Sólo sin orden de corte
          </label>
        </div>
      )}

      {deudoresQ.isPending && <Cargando />}
      {deudoresQ.isError && (
        <ErrorCarga
          error={deudoresQ.error}
          mensaje="Error al cargar deudores."
          onReintentar={() => void deudoresQ.refetch()}
        />
      )}

      {deudoresQ.data && total === 0 && (
        <EmptyState
          icon={AlertTriangle}
          tone="emerald"
          title="Sin morosos"
          description="Ningún socio tiene boletas impagas. Todo al día."
        />
      )}

      {total > 0 && filtrados.length === 0 && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-6 text-center text-sm text-neutral-600">
          Ningún deudor coincide con el filtro.
        </p>
      )}

      {filtrados.length > 0 && (
        <>
          {/* Móvil: tarjetas (auditoría A11Y-01). */}
          <ul className="space-y-2 md:hidden">
            {puedeGestionar && elegiblesFiltrados.length > 0 && (
              <li>
                <label className="inline-flex items-center gap-2 text-sm text-neutral-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-450"
                    checked={todosSeleccionados}
                    onChange={toggleTodos}
                  />
                  Seleccionar todos los que se pueden cortar
                </label>
              </li>
            )}
            {filtrados.map((d) => {
              const puede = elegible(d);
              const marcado = d.contrato_id != null && sel.has(d.contrato_id);
              return (
                <li
                  key={d.socio_id}
                  className="rounded-lg border border-neutral-200 bg-white p-3"
                >
                  <div className="flex items-start gap-3">
                    {puedeGestionar && (
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-neutral-450 disabled:opacity-40"
                        checked={marcado}
                        disabled={!puede}
                        onChange={() => d.contrato_id != null && toggle(d.contrato_id)}
                        aria-label={tituloCheckbox(d)}
                        title={tituloCheckbox(d)}
                      />
                    )}
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Link
                        to={`/socios/${d.socio_id}/cuenta-corriente`}
                        state={{ desde }}
                        className={`block ${LINK_SOCIO}`}
                      >
                        {d.socio_nombre}
                      </Link>
                      <div className="text-xs text-neutral-500">
                        {d.socio_numero ? `N° ${d.socio_numero} · ` : ""}
                        {d.sector_nombre ?? "Sin sector"} · {d.boletas_vencidas}{" "}
                        boleta(s)
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <SemaforoDeuda
                          meses={d.antiguedad_meses}
                          dias={diasAtraso(d)}
                          monto={d.monto_deuda}
                        />
                        <EstadoCorte d={d} desde={desde} />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Escritorio: tabla. */}
          <TableWrap label="Deudores" className="hidden md:block">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  {puedeGestionar && (
                    <th scope="col" className="w-10 px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-neutral-450"
                        checked={todosSeleccionados}
                        onChange={toggleTodos}
                        disabled={elegiblesFiltrados.length === 0}
                        aria-label="Seleccionar todos los que se pueden cortar"
                        title="Seleccionar todos los que se pueden cortar"
                      />
                    </th>
                  )}
                  <th scope="col" className="px-3 py-2 text-left">Socio</th>
                  <th scope="col" className="px-3 py-2 text-left">Sector</th>
                  <th scope="col" className="px-3 py-2 text-right">Boletas</th>
                  <th scope="col" className="px-3 py-2 text-right">Deuda</th>
                  <th scope="col" className="px-3 py-2 text-left">Atraso</th>
                  <th scope="col" className="px-3 py-2 text-left">Corte</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filtrados.map((d) => {
                  const puede = elegible(d);
                  const marcado = d.contrato_id != null && sel.has(d.contrato_id);
                  return (
                    <tr key={d.socio_id} className="hover:bg-neutral-50">
                      {puedeGestionar && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-neutral-450 disabled:opacity-40"
                            checked={marcado}
                            disabled={!puede}
                            onChange={() => d.contrato_id != null && toggle(d.contrato_id)}
                            aria-label={tituloCheckbox(d)}
                            title={tituloCheckbox(d)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <Link
                          to={`/socios/${d.socio_id}/cuenta-corriente`}
                          state={{ desde }}
                          className={LINK_SOCIO}
                        >
                          {d.socio_nombre}
                        </Link>
                        {d.socio_numero && (
                          <div className="text-xs text-neutral-500">N° {d.socio_numero}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{d.sector_nombre ?? "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
                        {d.boletas_vencidas}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">
                        {formatCLP(d.monto_deuda)}
                      </td>
                      <td className="px-3 py-2">
                        <SemaforoDeuda
                          meses={d.antiguedad_meses}
                          dias={diasAtraso(d)}
                          monto={d.monto_deuda}
                          mostrarMonto={false}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <EstadoCorte d={d} desde={desde} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}

      <ConfirmDialog
        open={confirmar}
        title={`Abrir ${sel.size} orden(es) de corte`}
        tone="danger"
        confirmLabel="Abrir cortes"
        loading={abrir.isPending}
        onCancel={() => setConfirmar(false)}
        onConfirm={() => abrir.mutate()}
        description={
          <>
            Se abrirán <strong>{sel.size}</strong> órdenes de corte por un total
            de <strong>{formatCLP(montoSeleccionado)}</strong>. Quedan como{" "}
            <strong>orden abierta</strong>: el agua no se corta todavía — después
            las notificas, programas y ejecutas desde Cortes. Si alguno ya tenía
            una orden, se salta solo.
          </>
        }
      />
    </div>
  );
}

function EstadoCorte({ d, desde }: { d: TopDeudor; desde: Desde }): JSX.Element {
  if (d.estado_corte != null) {
    return (
      <Badge tone={d.estado_corte === "Cortada" ? "rose" : "amber"}>
        <Scissors className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
        {etiquetaCorte(d.estado_corte)}
      </Badge>
    );
  }
  if (variosArranques(d)) {
    return (
      <Link
        to={`/socios/${d.socio_id}/cuenta-corriente#cortes`}
        state={{ desde }}
        className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
        title="Tiene varias casas con deuda: el corte se abre por casa desde su ficha"
      >
        Varias casas: ver ficha
        <ArrowRight className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
      </Link>
    );
  }
  return <span className="text-xs text-neutral-500">—</span>;
}
